import { useState } from 'react';
import {
  Box,
  IconButton,
  Chip,
  CircularProgress,
  TablePagination,
  Typography,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AddIcon from '@mui/icons-material/Add';
import { CheckCircle } from '@mui/icons-material';
import { DrawerControl, MetaDrivenTable, SearchField, DialogBox } from '../../../shared/components';
import { useFetchTask } from '../../tasks/hooks/useFetchTask';
import { RaiseTask } from '../../tasks/components/RaiseTask';
import { useUpdateChecklists } from '../../stageGates/mutation';

interface ActionItemsProps {
  meeting_id: string;
  meetingDetails?: any;
}

const ActionItems: React.FC<ActionItemsProps> = ({ meeting_id, meetingDetails }) => {

  const {
    isPending, taskData, pagination,
    searchDraft, handleChangeSearch, handleClear, handleSearch,
    changePage, changeRowsPerPage, refetch
  } = useFetchTask({ projectId: meeting_id, object_type: 'meeting', object_id: Number(meeting_id), my_tasks: false });

  const [raiseTask, setRaiseTask] = useState<boolean>(false);

  const [editTask, setEditTask] = useState({
    status: false,
    taskId: null as string | null
  });

  const [completeTask, setCompleteTask] = useState({
    status: false,
    taskId: null as string | null
  });

  const editChecklist = useUpdateChecklists();

  const onCompleteTask = async () => {
    await editChecklist.mutateAsync({
      checkListID: completeTask.taskId || '',
      payload: {
        status: 'completed',
        object_type: 'meeting',
        object_id: meeting_id,
      }
    });
    setCompleteTask({ status: false, taskId: null });
    refetch();
  };

  const headers = [
    { "alias_name": "task_ref", "label": "Action Ref", "display_order": 1, is_show: true },
    { "alias_name": "project_title", "label": "Project Title", "display_order": 2, is_show: true },
    { "alias_name": "task_name", "label": "Action Name", "display_order": 3, is_show: true },
    { "alias_name": "category", "label": "Category", "display_order": 4, is_show: true },
    { "alias_name": "description", "label": "Summary", "display_order": 5, is_show: true },
    { "alias_name": "due_date", "label": "Due Date", "display_order": 6, is_show: true },
    { "alias_name": "owner", "label": "Owner", "display_order": 7, is_show: true },
    { "alias_name": "status_display_text", "label": "Status", "display_order": 8, is_show: true },
    { "alias_name": "actions", "label": "Actions", "display_order": 9, is_show: true },
  ];

  const metrics = {
    tags_display_text: {
      type: 'component',
      element: (row: any) => (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {row.tags_display_text?.map((tag: string, index: number) => (
            <Chip key={index} label={tag} size="small" variant="outlined" />
          ))}
        </Box>
      )
    },
    due_date: {
      type: 'component',
      element: (row: any) => {
        const dueDate = new Date(row.due_date);
        return <Typography variant="body2">{dueDate.toLocaleDateString()}</Typography>;
      }
    },
    status_display_text: {
      type: 'component',
      element: (row: any) => {
        const isCompleted = row.status === 'completed';
        return (
          <Chip
            label={row.status_display_text}
            color={isCompleted ? 'success' : 'warning'}
            variant="outlined"
            size="small"
            icon={isCompleted ? <CheckCircle /> : <></>}
          />
        );
      }
    },
    actions: {
      type: 'component',
      element: (row: any) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit Task">
            <IconButton
              size="small"
              onClick={() => setEditTask({ status: true, taskId: row.task_id?.toString() || null })}
              disabled={row.status === 'completed'}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Complete Task">
            <IconButton
              size="small"
              onClick={() => setCompleteTask({ status: true, taskId: row.task_id?.toString() || null })}
              color="success"
              disabled={row.status === 'completed'}
            >
              <TaskAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  };

  const handleAddTask = () => {
    setRaiseTask(true);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, overflow: 'hidden' }}>
      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <SearchField
          searchTerm={searchDraft}
          handleSearch={handleChangeSearch}
          handleClearSearch={handleClear}
          handleApplySearch={handleSearch}
        />
        <Tooltip title="Add New Task">
          <IconButton onClick={handleAddTask} color="primary">
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {isPending && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      )}

      {!isPending && taskData && taskData.length > 0 && (
        <>
          <MetaDrivenTable headers={headers} dataMetrics={metrics} data={taskData} sortDisabled />
          {pagination && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={pagination.total || 0}
              rowsPerPage={pagination.limit || 10}
              page={(pagination.page || 1) - 1}
              onPageChange={(_, newPage) => changePage(newPage + 1)}
              onRowsPerPageChange={(event) => changeRowsPerPage(parseInt(event.target.value, 10))}
            />
          )}
        </>
      )}

      {!isPending && (!taskData || taskData.length === 0) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <Typography variant="body2" color="textSecondary">
            No action items found. Click the + button to create one.
          </Typography>
        </Box>
      )}

      <DrawerControl
        title={"Create Task"}
        subTitle={"Create a new action item for this meeting"}
        isOpen={raiseTask}
        handleClose={() => { setRaiseTask(false) }}
        children={
          <RaiseTask
            isEdit={false}
            taskId={null}
            onClose={() => { setRaiseTask(false) }}
            refetch={() => refetch()}
            object_type={'meeting'}
            object_id={meeting_id}
            enabledFields={meetingDetails.programme_id !== null ? ['action_category', 'meeting_programme'] : ['action_category', 'meeting_project']}
            meetingProjects={meetingDetails.sections.find((section: any) => section.update_key === 'projects')?.data.map((proj: any) => ({ value: proj.project_id, label: proj?.project_title?.toString() })) || []}
            meetingProgrammes={[{ value: meetingDetails.programme_id, label: meetingDetails.programme }]}
            disableFields={['tags', 'type']}
          />
        }
      />

      <DrawerControl
        title={"Edit Task"}
        subTitle={"Update action item details"}
        isOpen={editTask.status}
        handleClose={() => { setEditTask({ status: false, taskId: null }) }}
        children={
          <RaiseTask
            isEdit={true}
            taskId={editTask.taskId}
            onClose={() => { setEditTask({ status: false, taskId: null }) }}
            refetch={() => refetch()}
            object_type={'meeting'}
            enabledFields={['action_category']}
            disableFields={['tags']}
          />
        }
      />

      <DialogBox
        showModal={completeTask.status}
        title="Complete Action Item"
        subTitle=""
        onModalClose={function (): void {
          throw new Error('Function not implemented.');
        }}
        children={
          <Typography variant='body2'>
            Are you sure you want to mark this action item as complete?
          </Typography>
        }
        actions={
          [
            {
              label: 'Cancel',
              icon: 'reset' as const,
              variant: 'text' as const,
              handleClick: () => setCompleteTask({ status: false, taskId: null }),
            },
            {
              label: 'Complete',
              icon: 'success' as const,
              variant: 'contained' as const,
              handleClick: () => {
                onCompleteTask();

              },
            },
          ]
        }
      />


    </Box>
  );
};

export default ActionItems;
